import type { BankedProfile } from "./persistence";

export const SORTIE_CONTRACT_IDS = [
  "recovery",
  "redline",
  "gauntlet",
  "deep_hunt",
  "crown_gauntlet",
] as const;
export type SortieContractId = (typeof SORTIE_CONTRACT_IDS)[number];

export type SortieContractDef = {
  id: SortieContractId;
  name: string;
  hudLabel: string;
  description: string;
  minRank: number;
  salvageCost: number;
  salvageMult: number;
  threatBudgetMult: number;
  spawnCooldownMult: number;
  eliteChanceAdd: number;
  /** Fraction of carried salvage banked after a hull loss. */
  deathSalvageRetention: number;
  /** Multiplier for the delay to the first and each subsequent incursion. */
  incursionIntervalMult: number;
  /** Arena guardians that must be destroyed before extraction can begin. */
  requiredBossArenas: number;
};

/** Repeatable, single-sortie challenges. Buying one arms the next launch only. */
export const SORTIE_CONTRACTS: Record<SortieContractId, SortieContractDef> = {
  recovery: {
    id: "recovery",
    name: "Recovery Bond",
    hudLabel: "BONDED",
    description:
      "Banks 40% of carried salvage after a hull loss. Successful extracts are unchanged.",
    minRank: 18,
    salvageCost: 90,
    salvageMult: 1,
    threatBudgetMult: 1,
    spawnCooldownMult: 1,
    eliteChanceAdd: 0,
    deathSalvageRetention: 0.4,
    incursionIntervalMult: 1,
    requiredBossArenas: 0,
  },
  redline: {
    id: "redline",
    name: "Redline Contract",
    hudLabel: "REDLINE",
    description: "Heavier response and more elite signatures for a 35% salvage premium.",
    minRank: 26,
    salvageCost: 180,
    salvageMult: 1.35,
    threatBudgetMult: 1.3,
    spawnCooldownMult: 0.82,
    eliteChanceAdd: 0.06,
    deathSalvageRetention: 0,
    incursionIntervalMult: 1,
    requiredBossArenas: 0,
  },
  gauntlet: {
    id: "gauntlet",
    name: "Gauntlet Contract",
    hudLabel: "GAUNTLET",
    description:
      "Incursions arrive roughly twice as often with denser elite pressure for a 60% salvage premium.",
    minRank: 42,
    salvageCost: 420,
    salvageMult: 1.6,
    threatBudgetMult: 1.18,
    spawnCooldownMult: 0.9,
    eliteChanceAdd: 0.1,
    deathSalvageRetention: 0,
    incursionIntervalMult: 0.48,
    requiredBossArenas: 0,
  },
  deep_hunt: {
    id: "deep_hunt",
    name: "Deep Hunt Writ",
    hudLabel: "DEEP HUNT",
    description:
      "Extraction stays locked until one arena guardian is destroyed. Severe response pays a 125% salvage premium.",
    minRank: 70,
    salvageCost: 1500,
    salvageMult: 2.25,
    threatBudgetMult: 1.4,
    spawnCooldownMult: 0.76,
    eliteChanceAdd: 0.14,
    deathSalvageRetention: 0,
    incursionIntervalMult: 0.55,
    requiredBossArenas: 1,
  },
  crown_gauntlet: {
    id: "crown_gauntlet",
    name: "Crown Gauntlet Writ",
    hudLabel: "CROWN RUN",
    description:
      "Extraction stays locked until two arena guardians are destroyed. Extreme response pays a 175% salvage premium.",
    minRank: 90,
    salvageCost: 3000,
    salvageMult: 2.75,
    threatBudgetMult: 1.55,
    spawnCooldownMult: 0.68,
    eliteChanceAdd: 0.2,
    deathSalvageRetention: 0,
    incursionIntervalMult: 0.42,
    requiredBossArenas: 2,
  },
};

export type ContractPurchaseResult =
  | { ok: true; profile: BankedProfile; contract: SortieContractDef; message: string }
  | { ok: false; message: string };

export function isSortieContractId(value: unknown): value is SortieContractId {
  return typeof value === "string" && SORTIE_CONTRACT_IDS.includes(value as SortieContractId);
}

export function canQueueSortieContract(
  profile: BankedProfile,
  contractId: SortieContractId,
): { ok: true; contract: SortieContractDef } | { ok: false; message: string } {
  const contract = SORTIE_CONTRACTS[contractId];
  if (profile.queuedSortieContract) {
    return { ok: false, message: "A sortie contract is already armed." };
  }
  if (profile.pilotMeta.rank < contract.minRank) {
    return { ok: false, message: `Requires Pilot Rank ${contract.minRank}.` };
  }
  if (profile.salvage < contract.salvageCost) {
    return { ok: false, message: `Need ${contract.salvageCost} banked salvage.` };
  }
  return { ok: true, contract };
}

export function queueSortieContract(
  profile: BankedProfile,
  contractId: SortieContractId,
): ContractPurchaseResult {
  const gate = canQueueSortieContract(profile, contractId);
  if (!gate.ok) return gate;
  return {
    ok: true,
    contract: gate.contract,
    profile: {
      ...profile,
      salvage: profile.salvage - gate.contract.salvageCost,
      queuedSortieContract: contractId,
    },
    message: `${gate.contract.name} armed for the next launch.`,
  };
}

export function activeSortieContract(
  contractId: SortieContractId | null,
): SortieContractDef | null {
  return contractId ? SORTIE_CONTRACTS[contractId] : null;
}

export function contractHudValue(contract: SortieContractDef, bossArenasCleared = 0): string {
  if (contract.requiredBossArenas > 0) {
    return `${Math.min(bossArenasCleared, contract.requiredBossArenas)}/${contract.requiredBossArenas} BOSS`;
  }
  if (contract.deathSalvageRetention > 0) {
    return `${Math.round(contract.deathSalvageRetention * 100)}% SAVE`;
  }
  return `+${Math.round((contract.salvageMult - 1) * 100)}%`;
}

export function contractRunSummary(contract: SortieContractDef): string {
  if (contract.requiredBossArenas > 0) {
    return `clear ${contract.requiredBossArenas} arena guardian before extraction, +${Math.round((contract.salvageMult - 1) * 100)}% salvage`;
  }
  if (contract.deathSalvageRetention > 0) {
    return `${Math.round(contract.deathSalvageRetention * 100)}% cargo recovery on hull loss`;
  }
  return `+${Math.round((contract.salvageMult - 1) * 100)}% salvage, heavier response`;
}

export function contractBossObjectiveMet(
  contract: SortieContractDef | null,
  bossArenasCleared: number,
): boolean {
  return !contract || bossArenasCleared >= contract.requiredBossArenas;
}
